/* A real cart gift, not a price override. Discounts remain owned by Shiprocket. */
(() => {
  if (window.DesiDanaGheeOffer) return;
  const element = document.getElementById('desi-dana-ghee-offer-config');
  if (!element) return;
  const config = JSON.parse(element.textContent);
  const giftId = String(config.giftVariantId);
  const qualifyingId = String(config.qualifyingVariantId);
  const root = `${config.root || '/'}`.replace(/\/?$/, '/');
  let queue = Promise.resolve();
  const enqueue = (task) => {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  };
  const isGift = (item) => String(item.variant_id ?? item.id) === giftId;
  const describe = (cart) => {
    const items = cart.items || [];
    const eligibleQuantity = config.enabled ? items.reduce((sum, item) =>
      sum + (String(item.variant_id ?? item.id) === qualifyingId ? item.quantity : 0), 0) : 0;
    const gifts = items.filter(isGift);
    const giftQuantity = gifts.reduce((sum, item) => sum + item.quantity, 0);
    return { eligibleQuantity, giftQuantity, gifts, complete: eligibleQuantity === giftQuantity,
      giftCharge: gifts.reduce((sum, item) => sum + (item.final_line_price ?? item.line_price ?? 0), 0) };
  };
  async function request(path, body) {
    const options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      if (body instanceof FormData) options.body = body;
      else {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      }
    }
    const response = await fetch(`${root}cart${path}.js`, options);
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Your cart could not be updated.');
    return data;
  }
  async function reconcile(cart) {
    let warning = '';
    try {
      const state = describe(cart);
      // Normalize by line key, never by a changing line index. No mutation if already correct.
      if (!state.complete) {
        if (!state.gifts.length && state.eligibleQuantity) {
          await request('/add', { items: [{ id: Number(giftId), quantity: state.eligibleQuantity }] });
          cart = await request('');
        } else if (state.gifts.length) {
          // Remove surplus lines first. Re-read keys after each mutation (discounts can change keys).
          while (cart.items.filter(isGift).length > 1) {
            const gift = cart.items.filter(isGift).at(-1);
            cart = await request('/change', { id: gift.key, quantity: 0 });
          }
          const gift = cart.items.find(isGift);
          if (gift) cart = await request('/change', { id: gift.key, quantity: state.eligibleQuantity });
        }
      }
    } catch (error) {
      // The paid item has already been added: never report that the entire add failed.
      try { cart = await request(''); } catch (_) { /* Keep the last confirmed cart. */ }
      warning = `The 125 ml gift could not be added or updated. ${error.message} Please contact us before checkout.`;
    }
    if (!describe(cart).complete && !warning) warning = 'The 125 ml gift is not ready. Please refresh your cart or contact us before checkout.';
    return { cart, warning };
  }
  const api = window.DesiDanaGheeOffer = {
    isGift, describe,
    add: (data) => enqueue(async () => {
      if (String(data.get('id')) === giftId) throw new Error('125 ml ghee is a complimentary gift with 1 LTR ghee and cannot be purchased separately.');
      const result = await request('/add', data);
      const state = await reconcile(await request(''));
      return { ...state, result };
    }),
    change: (key, quantity) => enqueue(async () => reconcile(await request('/change', { id: key, quantity }))),
    sync: () => enqueue(async () => reconcile(await request(''))),
  };

  // Shiprocket's product-page button must use the actual paired cart for this offer.
  // Capture before its inline buyProduct handler; preserve other products/sizes unchanged.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-ppi-buy-now], .shiprocket-headless[data-type="product"] .sr-headless-checkout');
    if (!button) return;
    const form = button.closest('form') || button.closest('premium-product-information')?.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const data = new FormData(form);
    const id = String(data.get('id'));
    if (id !== giftId && (id !== qualifyingId || !config.enabled)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const { cart, warning } = await api.add(data);
      if (warning || !describe(cart).complete) throw new Error(warning || 'Your complimentary gift is not ready. Please contact us before checkout.');
      if (typeof window.shiprocketCheckoutEvents?.buyCart !== 'function') throw new Error('Shiprocket checkout is still loading. Please try Buy now in your cart.');
      window.shiprocketCheckoutEvents.buyCart(event);
    } catch (error) {
      const header = document.querySelector('premium-custom-header');
      if (header?.openCartDrawer) {
        header.openCartDrawer();
        // loadCart will also show any inventory warning; retain a checkout SDK error separately.
      }
      let notice = form.querySelector('[data-ghee-offer-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.gheeOfferError = '';
        notice.setAttribute('role', 'alert');
        form.append(notice);
      }
      notice.textContent = error.message;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }, true);

  // Guard native/quick-add forms as well as the premium section integration.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    const data = new FormData(form);
    const id = String(data.get('id'));
    if (id !== giftId && (id !== qualifyingId || !config.enabled)) return;
    if (id !== giftId && form.closest('premium-collection-showcase, premium-product-information')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button?.disabled) return;
    if (button) button.disabled = true;
    try {
      await api.add(data);
      const header = document.querySelector('premium-custom-header');
      if (header?.openCartDrawer) header.openCartDrawer();
      else window.location.assign(`${root}cart`);
    } catch (error) {
      let notice = form.querySelector('[data-ghee-offer-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.dataset.gheeOfferError = '';
        notice.setAttribute('role', 'alert');
        form.append(notice);
      }
      notice.textContent = error.message;
    } finally { if (button) button.disabled = false; }
  }, true);

  // Native cart quantity changes are reconciled too; no polling or fetch interception.
  document.addEventListener('shopify:cart:lines-update', () => {
    api.sync().then(({ cart, warning }) => {
      const header = document.querySelector('premium-custom-header');
      if (header?.cartDrawer?.open) {
        header.renderCart(cart);
        if (warning) header.setCartStatus(warning, true);
      }
    }).catch(() => {});
  });
  if (config.cartHasOfferItems) api.sync().catch(() => {});
})();
